import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import zxcvbn from 'zxcvbn';
import {
  RouteProps,
} from 'react-router-dom';
import Checkbox from '@material-ui/core/Checkbox';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  FormContainer,
  TextFieldContainer,
  StyledTextField,
} from './FormStyles';
import { emailRegex } from '../../utils/Regex';
import { register } from '../../api/api';
import { useVendingMachine } from '../../contexts/VendingMachineContext';
import { passwordStrengthColors, passwordStrengthLabels } from '../../constants/User';
import NotificationToast from '../notifications/NotificationToast';
import i18n from '../../i18n/i18n';
import { useTheme } from '../../contexts/ThemeContext';
import { countryData } from '../../constants/CountryData';
import { getVatNumberError } from '../../utils/vatValidation';
import { formatServerError } from '../../utils/MessageFormatter';
import TaxCountryModal from '../modals/TaxCountryModal';
import { TaxId } from '../../types/types';

type Error = {
  name: string | null,
  email: string | null,
  taxId: string | null,
  password: string | null,
  confirmPassword: string | null,
}

type RoutePropsTypes = RouteProps<string> & {
  // eslint-disable-next-line react/no-unused-prop-types
  handleError: () => void
}

const StyledButton = styled(({ ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${(props) => props.color};
    color: ${(props) => props.textColor};
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 120px;
    text-transform: capitalize;
    font-size: 0.85em;

    ${({ disabled }) => disabled && `
     background-color: rgb(243, 243, 243);
     color: rgba(255,255,255,0.15)
     border-width: 0;
     border-radius: 10px;
     padding: 10px 20px;
     margin: 10px;
     width: 120px;
     text-transform: capitalize;
     font-size: 0.85em;
  `
}
`;

const StyledDiv = styled.div`
    padding-top: 20px;
    padding-bottom: 20px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  flex:1;
`;

const StyledLink = styled.a`
 text-decoration: underline;
  color: black;
`;

const StyledCheckbox = styled(({ ...rest }) => (
  <Checkbox
    {...rest}
  />
))`
  && {
  ${({ checked, color }) => checked && `
     color: ${color};
  `
}
`;

const StyledSpan = styled.span`
    color: #d01818;
`;

const HelexiaInputWrapper = styled.div`
  background: #efefef;
  border-radius: 12px;
  height: 36px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const HelexiaInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #3f3f3f;
  font-family: Geologica, sans-serif;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.98px;
  width: 100%;
  &::placeholder {
    color: #6a6a6a;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #efefef inset;
    box-shadow: 0 0 0px 1000px #efefef inset;
    -webkit-text-fill-color: #3f3f3f;
  }
`;

const RegisterForm = ({ history, initialState }:RoutePropsTypes) => {
  const [name, setName] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState<string | null>(initialState?.email);
  const [password, setPassword] = React.useState<string | null>(initialState?.password);
  const [taxId, setTaxId] = React.useState<TaxId>({ prefix: countryData.PT?.prefix || null, number: null });
  const [isSelectingCountry, setSelectingCountry] = useState(false);
  const [country, setCountry] = useState('PT');
  const [confirmPassword, setConfirmPassword] = React.useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error>({
    name: null,
    email: null,
    taxId: null,
    password: null,
    confirmPassword: null,
  });
  const [hasError, setHasError] = React.useState(false);
  const [notification, setNotification] = useState<any>(null);
  const [showNotification, setShowNotification] = useState(false);
  const { theme, themeName } = useTheme();
  const isHelexia = themeName === 'helexia';
  const isGalp = themeName === 'galp';

  const CountryFlag = countryData[country]?.component;

  const handleError = useCallback((newError) => {
    setHasError(newError);
  }, []);

  const { stickerId } = useVendingMachine();

  const getPasswordStrength = (text?: string | null) => {
    const score = Math.min(zxcvbn(text).score, 3);
    return {
      score,
      message: i18n.t(passwordStrengthLabels[score]),
      color: passwordStrengthColors[score],
    };
  };

  const handleCountrySelected = useCallback((selectedCountry) => {
    setCountry(selectedCountry);
    setTaxId({ prefix: countryData[selectedCountry]?.prefix, number: '' });
    setSelectingCountry(false);
  }, []);

  useEffect(() => {
    // Rebuild every field from scratch — carrying the previous message over and
    // then guarding on it made the errors sticky (they never cleared).
    // `taxId` is owned by its own onChange handler, so it is preserved as-is.
    const newErrors: Error = {
      ...error,
      name: null,
      email: null,
      password: null,
      confirmPassword: null,
    };

    if (!name) {
      newErrors.name = i18n.t('mandatory');
    }

    if (!email) {
      newErrors.email = i18n.t('mandatory');
    } else if (!emailRegex.test(email)) {
      newErrors.email = i18n.t('invalid_email');
    }

    if (!password) {
      newErrors.password = i18n.t('mandatory');
    } else if (getPasswordStrength(password).score < 3) {
      newErrors.password = i18n.t('password_too_simple');
    }

    if (confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = i18n.t('different_password');
    }

    handleError(
      !confirmPassword
      || !!newErrors.name
      || !!newErrors.email
      || !!newErrors.password
      || !!newErrors.confirmPassword,
    );

    setError(newErrors);
  }, [name, email, password, confirmPassword, handleError]);

  const onSubmit = async (e) => {
    if (acceptedTerms === false) {
      setNotification({
        severity: 'error',
        title: i18n.t('error'),
        message: i18n.t('must_accept_terms'),
      });
      setShowNotification(true);
    }

    e.preventDefault();

    if (acceptedTerms) {
      const nifError = getVatNumberError(taxId.prefix, taxId.number);
      if (nifError) {
        setError((prev) => ({ ...prev, taxId: i18n.t(nifError) }));
        return;
      }
      try {
        const trimmedEmail = email ? email.trim() : '';
        const fullTaxId = taxId.prefix && taxId.number ? taxId.prefix + taxId.number : null;
        if (stickerId) {
          await register(name, trimmedEmail, password, stickerId, fullTaxId, themeName);
        } else {
          await register(name, trimmedEmail, password, undefined, fullTaxId, themeName);
        }
        handleError(false);
        setNotification({
          severity: 'success',
          title: i18n.t('success'),
          message: i18n.t('check_email'),
        });
        setShowNotification(true);
        setTimeout(() => history.push('home'), 5000);
      } catch (ex) {
        setNotification({
          severity: 'error',
          title: i18n.t('error'),
          message: await formatServerError(ex),
        });
        setShowNotification(true);
      }
    }
  };

  let passwordStrength;
  if (password) {
    passwordStrength = getPasswordStrength(password);
  }

  if (isGalp) {
    const galpFieldGroup: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      width: '100%',
    };
    const galpLabel: React.CSSProperties = {
      fontSize: '14px',
      lineHeight: '22px',
      color: '#111110',
      margin: 0,
    };
    const galpInputBox: React.CSSProperties = {
      border: '2px solid #bab9ba',
      borderRadius: '14px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '0 10px',
      boxSizing: 'border-box',
      width: '100%',
    };
    const galpInput: React.CSSProperties = {
      border: 'none',
      outline: 'none',
      background: 'transparent',
      width: '100%',
      fontFamily: theme.bodyFont,
      fontSize: '16px',
      color: '#0b0b0b',
    };
    const galpErrorStyle: React.CSSProperties = { color: '#d01818', fontSize: '12px', margin: '4px 0 0 0' };
    const submitDisabled = hasError || !acceptedTerms || !!getVatNumberError(taxId.prefix, taxId.number);

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: theme.bodyFont,
      }}
      >
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
        />
        <TaxCountryModal
          isSelectingCountry={isSelectingCountry}
          setSelectingCountry={setSelectingCountry}
          handleCountrySelected={handleCountrySelected}
          country={country}
        />

        {/* Header — back, centered title, avatar */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            aria-label="back"
            onClick={() => history.push('/')}
            style={{
              position: 'absolute',
              left: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 1L1 6L6 11"
                stroke="#0b0b0b"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#0b0b0b',
            margin: 0,
          }}
          >
            {i18n.t('register')}
          </p>
          <div style={{
            position: 'absolute',
            right: '16px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#fbfbfb',
            border: '1px solid #919192',
            boxShadow: '0px 1px 2px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
          >
            <img
              src="/companies/GALP/user_icon.svg"
              alt="user"
              style={{ width: '15px', height: '14px', display: 'block' }}
            />
          </div>
        </div>

        {/* Body — scrollable fields */}
        <form
          id="register"
          noValidate
          autoComplete="off"
          onSubmit={onSubmit}
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '13px',
            padding: '24px 16px',
            boxSizing: 'border-box',
          }}
        >
          <div style={galpFieldGroup}>
            <label htmlFor="galp-name" style={galpLabel}>{i18n.t('name')}</label>
            <div style={galpInputBox}>
              <input
                id="galp-name"
                type="text"
                value={name || ''}
                onChange={(e) => setName(e.target.value)}
                style={galpInput}
              />
            </div>
            {error.name && error.name !== i18n.t('mandatory') && (
              <p style={galpErrorStyle}>{error.name}</p>
            )}
          </div>

          <div style={galpFieldGroup}>
            <label htmlFor="galp-email" style={galpLabel}>{i18n.t('email')}</label>
            <div style={galpInputBox}>
              <input
                id="galp-email"
                type="email"
                value={email || ''}
                onChange={(e) => setEmail(e.target.value)}
                style={galpInput}
              />
            </div>
            {error.email && error.email !== i18n.t('mandatory') && (
              <p style={galpErrorStyle}>{error.email}</p>
            )}
          </div>

          <div style={galpFieldGroup}>
            <label htmlFor="galp-nif" style={galpLabel}>
              {i18n.t('vat_number')}
              {' (NIF)'}
            </label>
            <div style={galpInputBox}>
              <button
                type="button"
                aria-label="select country"
                onClick={() => setSelectingCountry(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {country === 'PT' ? (
                  <img
                    src="/companies/GALP/flag_pt.svg"
                    alt="PT"
                    style={{ width: '28px', height: '20px', display: 'block' }}
                  />
                ) : (
                  CountryFlag && (
                    <span style={{
                      width: '28px',
                      height: '20px',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      display: 'block',
                    }}
                    >
                      <CountryFlag style={{ width: '28px', height: '20px', display: 'block' }} />
                    </span>
                  )
                )}
              </button>
              <input
                id="galp-nif"
                type="text"
                value={taxId.number || ''}
                onChange={(e) => {
                  const number = e.target.value;
                  const taxIdError = getVatNumberError(taxId.prefix, number);
                  setTaxId((prev) => ({ ...prev, number }));
                  setError((prevState) => ({
                    ...prevState,
                    taxId: taxIdError ? i18n.t(taxIdError) : null,
                  }));
                }}
                style={galpInput}
              />
            </div>
            {error.taxId && <p style={galpErrorStyle}>{error.taxId}</p>}
          </div>

          <div style={galpFieldGroup}>
            <label htmlFor="galp-password" style={galpLabel}>{i18n.t('password')}</label>
            <div style={galpInputBox}>
              <input
                id="galp-password"
                type="password"
                value={password || ''}
                onChange={(e) => setPassword(e.target.value)}
                style={galpInput}
              />
            </div>
            {error.password && error.password !== i18n.t('mandatory') && (
              <p style={galpErrorStyle}>{error.password}</p>
            )}
          </div>

          <div style={galpFieldGroup}>
            <label htmlFor="galp-confirm" style={galpLabel}>{i18n.t('confirm_password')}</label>
            <div style={galpInputBox}>
              <input
                id="galp-confirm"
                type="password"
                value={confirmPassword || ''}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={galpInput}
              />
            </div>
            {error.confirmPassword && error.confirmPassword !== i18n.t('mandatory') && (
              <p style={galpErrorStyle}>{error.confirmPassword}</p>
            )}
          </div>

          {/* Terms — not in the Figma, required for registration (RGPD) */}
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
            marginTop: '4px',
          }}
          >
            <input
              type="checkbox"
              id="galp-terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{
                marginTop: '2px',
                flexShrink: 0,
                width: '16px',
                height: '16px',
                accentColor: '#be2e00',
                cursor: 'pointer',
              } as React.CSSProperties}
            />
            <label
              htmlFor="galp-terms"
              style={{
                fontSize: '13px',
                color: '#6b6b6b',
                margin: 0,
                cursor: 'pointer',
              }}
            >
              {i18n.t('terms_and_conditions1')}
              {' '}
              <a
                href="https://vendiot-static.elecctro.com/customer/privacy_policy.html"
                style={{ color: theme.linkColor, textDecoration: 'underline' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {i18n.t('terms_and_conditions2')}
              </a>
            </label>
          </div>
        </form>

        {/* Footer — fixed Registar button */}
        <div style={{
          borderTop: '1px solid #bababa',
          background: 'white',
          height: '66px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 32px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="submit"
            form="register"
            disabled={submitDisabled}
            style={{
              height: '36px',
              width: '100%',
              borderRadius: '57px',
              background: submitDisabled ? '#df9680' : theme.buttonColor,
              border: 'none',
              color: theme.buttonTextColor,
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
            }}
          >
            {i18n.t('register')}
          </button>
        </div>
      </div>
    );
  }

  if (isHelexia) {
    const helexiaErrorStyle: React.CSSProperties = {
      color: '#d01818',
      fontFamily: 'Geologica, sans-serif',
      fontWeight: 500,
      fontSize: '12px',
      margin: '4px 0 0 0',
    };
    return (
      <>
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
        />
        <TaxCountryModal
          isSelectingCountry={isSelectingCountry}
          setSelectingCountry={setSelectingCountry}
          handleCountrySelected={handleCountrySelected}
          country={country}
        />
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%',
        }}
        >
          <img
            src="/companies/HELEXIA/helexia_footer_logo.svg"
            alt="Helexia"
            style={{ width: '112px', height: '25px' }}
          />
          <p style={{
            fontFamily: 'Geologica, sans-serif',
            fontWeight: 600,
            fontSize: '32px',
            lineHeight: '32px',
            color: '#004e74',
            textAlign: 'center',
            margin: 0,
            width: '100%',
          }}
          >
            {i18n.t('register_helexia')}
          </p>
          <form
            onSubmit={onSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
              <div>
                <HelexiaInputWrapper>
                  <HelexiaInput
                    type="text"
                    placeholder={`${i18n.t('name')} *`}
                    value={name || ''}
                    onChange={(e) => setName(e.target.value)}
                  />
                </HelexiaInputWrapper>
                {error.name && error.name !== i18n.t('mandatory') && <p style={helexiaErrorStyle}>{error.name}</p>}
              </div>
              <div>
                <HelexiaInputWrapper>
                  <HelexiaInput
                    type="email"
                    placeholder="Email *"
                    value={email || ''}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </HelexiaInputWrapper>
                {error.email && error.email !== i18n.t('mandatory') && <p style={helexiaErrorStyle}>{error.email}</p>}
              </div>
              <div>
                <div style={{
                  background: '#efefef',
                  borderRadius: '12px',
                  height: '36px',
                  padding: '0 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                >
                  <button
                    type="button"
                    onClick={() => setSelectingCountry(true)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      flexShrink: 0, width: '25px', height: '25px', borderRadius: '50%', overflow: 'hidden', position: 'relative',
                    }}
                  >
                    {CountryFlag && (
                      <CountryFlag style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '40px', height: '40px' }} />
                    )}
                  </button>
                  <HelexiaInput
                    type="text"
                    placeholder={i18n.t('vat_number') as string}
                    value={taxId.number || ''}
                    onChange={(e) => {
                      const number = e.target.value;
                      const taxIdError = getVatNumberError(taxId.prefix, number);
                      setTaxId((prev) => ({ ...prev, number }));
                      setError((prevState) => ({
                        ...prevState,
                        taxId: taxIdError ? i18n.t(taxIdError) : null,
                      }));
                    }}
                  />
                </div>
                {error.taxId && <p style={helexiaErrorStyle}>{error.taxId}</p>}
              </div>
              <div>
                <HelexiaInputWrapper>
                  <HelexiaInput
                    type="password"
                    placeholder={`${i18n.t('create_password')} *`}
                    value={password || ''}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </HelexiaInputWrapper>
                {error.password && error.password !== i18n.t('mandatory') && <p style={helexiaErrorStyle}>{error.password}</p>}
              </div>
              <div>
                <HelexiaInputWrapper>
                  <HelexiaInput
                    type="password"
                    placeholder={`${i18n.t('confirm_password')} *`}
                    value={confirmPassword || ''}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </HelexiaInputWrapper>
                {error.confirmPassword && error.confirmPassword !== i18n.t('mandatory') && <p style={helexiaErrorStyle}>{error.confirmPassword}</p>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                id="helexia-terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{
                  marginTop: '2px', flexShrink: 0, width: '18px', height: '18px',
                  accentColor: '#004e74', cursor: 'pointer',
                } as React.CSSProperties}
              />
              <label
                htmlFor="helexia-terms"
                style={{
                  fontFamily: 'Geologica, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#6b6b6b',
                  letterSpacing: '-0.98px',
                  margin: 0,
                  cursor: 'pointer',
                }}
              >
                {i18n.t('terms_and_conditions1')}
                {' '}
                <a
                  href="https://vendiot-static.elecctro.com/customer/privacy_policy.html"
                  style={{ color: '#004e74', textDecoration: 'underline' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.t('terms_and_conditions2')}
                </a>
              </label>
            </div>
            <button
              type="submit"
              disabled={hasError || !acceptedTerms}
              style={{
                background: (hasError || !acceptedTerms) ? '#d0d0d0' : 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
                borderRadius: '12px',
                height: '36px',
                border: 'none',
                cursor: (hasError || !acceptedTerms) ? 'not-allowed' : 'pointer',
                color: 'white',
                fontFamily: 'Geologica, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                letterSpacing: '-0.42px',
                width: '100%',
              }}
            >
              {i18n.t('register')}
            </button>
            <p style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              color: '#6b6b6b',
              letterSpacing: '-0.98px',
              textAlign: 'center',
              margin: 0,
            }}
            >
              {i18n.t('already_have_account')}
              {' '}
              <span
                role="button"
                tabIndex={0}
                style={{ color: '#004e74', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => history.push('/')}
                onKeyDown={(e) => e.key === 'Enter' && history.push('/')}
              >
                {i18n.t('login')}
              </span>
            </p>
          </form>
        </div>
      </>
    );
  }

  return (
    <FormContainer>
      <NotificationToast
        open={showNotification}
        message={notification && notification.message}
        title={notification && notification.title}
        severity={notification && notification.severity}
        onClose={() => setShowNotification(false)}
      />
      <form noValidate autoComplete="off" onSubmit={onSubmit} id="register" style={{ color: 'rgba(77,76,77,255)' }}>
        <StyledDiv>
          <span>{i18n.t('name')}</span>
          <StyledTextField
            fullWidth
            onChange={(e) => setName(e.target.value)}
            error={!!error?.name}
            id="name"
            value={name}
            helperText={<StyledSpan>{error.name ? error.name : ''}</StyledSpan>}
          />
        </StyledDiv>
        <StyledDiv>
          <span>Email</span>
          <StyledTextField
            fullWidth
            onChange={(e) => setEmail(e.target.value)}
            error={!!error?.email}
            id="email"
            value={email}
            helperText={<StyledSpan>{error.email ? error.email : ''}</StyledSpan>}
          />
        </StyledDiv>
        <TaxCountryModal
          isSelectingCountry={isSelectingCountry}
          setSelectingCountry={setSelectingCountry}
          handleCountrySelected={handleCountrySelected}
          country={country}
        />
        <StyledDiv>
          <span>{i18n.t('vat_number')}</span>
          <StyledTextField
            fullWidth
            onChange={(e) => {
              const number = e.target.value;
              const taxIdError = getVatNumberError(taxId.prefix, number);

              setTaxId((prev) => ({ ...prev, number }));
              setError((prevState) => ({
                ...prevState,
                taxId: taxIdError ? i18n.t(taxIdError) : null,
              }));
            }}
            error={!!error?.taxId}
            id="taxId"
            value={taxId.number}
            helperText={<StyledSpan>{error.taxId ? error.taxId : ''}</StyledSpan>}
            style={{
              margin: '5px 0px',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  style={{
                    width: '50px',
                    paddingBottom: '5px',
                  }}
                  position="start"
                  onClick={() => setSelectingCountry(true)}
                >
                  <CountryFlag
                    style={{
                      borderRadius: '5px',
                      width: '100%',
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('password')}</span>
          <StyledTextField
            fullWidth
            value={password}
            type="password"
            onChange={(e) => setPassword(e.target.value)}
            error={!!error.password}
            id="password"
            helperText={(
              <>
                {(
                  password && (
                    <LinearProgress
                      style={{ backgroundColor: passwordStrength.color ? passwordStrength.color : '#20e538' }}
                      value={100 - ((passwordStrength.score + 1) * 25)}
                      variant="determinate"
                    />
                  )
                )}
                <StyledSpan>{error.password ? error.password : ' '}</StyledSpan>
              </>
            )}
            InputProps={{
              endAdornment: password ? (
                <InputAdornment position="end">
                  <span style={{ color: passwordStrength.color }}>
                    {passwordStrength.message}
                  </span>
                </InputAdornment>
              ) : null,
            }}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('confirm_password')}</span>
          <StyledTextField
            fullWidth
            type="password"
            color="secondary"
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!error.confirmPassword}
            id="confirmPassword"
            helperText={<StyledSpan>{error.confirmPassword ? error.confirmPassword : ''}</StyledSpan>}
          />
        </StyledDiv>
        <TextFieldContainer>
          <StyledCheckbox
            checked={acceptedTerms}
            color={theme.buttonColor}
            onChange={(event) => {
              setAcceptedTerms(event.currentTarget.checked);
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <small>
              {i18n.t('terms_and_conditions1')}
              &nbsp;
              <StyledLink
                href="https://vendiot-static.elecctro.com/customer/privacy_policy.html"
              >
                {i18n.t('terms_and_conditions2')}
              </StyledLink>
            </small>
          </div>
        </TextFieldContainer>
        <ButtonsContainer>
          <StyledButton
            variant="outlined"
            color={theme.buttonColor}
            textColor={theme.buttonTextColor}
            type="submit"
            form="register"
            disabled={hasError}
          >
            {i18n.t('register')}
          </StyledButton>
        </ButtonsContainer>
      </form>
    </FormContainer>
  );
};

export default withRouter(RegisterForm);
